import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { EntityHelper } from "@/common/helpers/entity";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useDisplayProps } from "@/common/hooks/useDisplayProps";
import { AccessoryCheckDtoAll } from "@/common/mapping/mappers/accessoryCheck";
import { apiClient } from "@/core/api/ApiClient";
import {
  AccessoryCheckDto,
  AccessoryCheckItemDto,
  AccessoryCheckItemReferenceDto,
  AccessoryCheckReferenceDto,
} from "@/core/api/generated/v1";

import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import { getContractInlineTitleShort } from "../Contract/ContractInline";
import { getVehicleInlineTitleShort } from "../Vehicle/VehicleInline";
import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../components/EntityInline/BaseEntityInline";
import { EntityInlineActionIcon } from "../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../components/EntityInline/EntityInlineActions";
import { AccessoryCheckViewModal } from "./View/AccessoryCheckViewModal";

const defaultDisplayProps = { vehicle: true, contract: true };
type DisplayProps = typeof defaultDisplayProps;

export const getAccessoryCheckInlineTitle = (
  data: Nil<AccessoryCheckDtoAll>,
  displayProps?: DisplayProps,
) =>
  data
    ? StringHelper.join(
        [
          EntityHelper.getNumberTitle(data),
          displayProps?.vehicle && data.vehicle
            ? getVehicleInlineTitleShort(data.vehicle)
            : undefined,
          displayProps?.contract && data.contract
            ? getContractInlineTitleShort(data.contract)
            : undefined,
        ],
        ", ",
        {
          skipEmpty: true,
        },
      )
    : undefined;

export interface AccessoryCheckInlineProps extends BaseEntityInlineInheritableProps {
  entity: AccessoryCheckDto | AccessoryCheckReferenceDto | null | undefined;
  entityId?: string | null;
  item?: AccessoryCheckItemDto | AccessoryCheckItemReferenceDto;
  itemId?: string | null;
  displayProps?: Partial<DisplayProps>;
}

export default function AccessoryCheckInline({
  entity,
  entityId,
  item,
  itemId,
  displayProps,
  fetch = true,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: AccessoryCheckInlineProps) {
  const displayPropsComputed = useDisplayProps([defaultDisplayProps, displayProps]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.accessoryChecksApi.apiV1AccessoryChecksAccessoryCheckIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      accessoryCheckId: entityId!,
    },
    {
      deps: [entityId],
      skip: !fetch || !entityId || !!entity,
    },
  );
  entity = request?.data || entity;

  const entityIdComputed = entity?.id || entityId;
  const full = entity as AccessoryCheckDto;
  // const ref = entity as AccessoryCheckReferenceDto;

  item = item || (itemId ? full?.items?.find((x) => x.id === itemId) : undefined);

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={fetch ? request : undefined}
        icon={<AppIcon of='accessoryCheck' inText />}
        content={
          <>
            {getAccessoryCheckInlineTitle(entity, displayPropsComputed)}
            {item && <> (#{item.localNumber})</>}
            {!entity && !item && <>Accessory Check</>}
          </>
        }
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
        tooltipTitle={otherProps?.tooltipTitle || "Accessory check"}
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <AccessoryCheckViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                accessoryCheckId: entityIdComputed,
                accessoryCheck: EntityHelper.nullIfNotFull(entity),
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
